// The staff side: signing in with a work account and composing the standing pages a
// buyer sees.
//
// Everything here is OPTIONAL to the protocol — a company that never opens this surface
// is still conformant. It lives in the buyer's app because that is where the renderer
// lives, and a page editor that previews with a different renderer than the buyer's is
// an editor that lies.
//
// Authentication mirrors the buyer's: a token AND a device signature on every call, so a
// token lifted out of memory is worth nothing without the key. The key is an ordinary
// device key under a reserved alias — staff and buyer keys are separate by construction,
// never the same key wearing two hats.

import type { PublicKeyInfo } from '$lib/crypto/device-keys';

import { joinPath, send } from './http';
import { parseElements, type MessageElement } from './types';

/** The alias the staff device key lives under. Reserved: no account id can collide with
 *  it, because account ids never start with a bracket. */
export const STAFF_KEY_ALIAS = '[staff]';

export interface StaffSession {
  token: string;
  expiresAtMs: number;
  operatorName: string;
}

export interface StaffThread {
  threadId: string;
  buyerName: string;
  lastEventTimeMs: number | undefined;
}

export interface StaffView {
  viewId: string;
  title: string;
  elements: MessageElement[];
  rawElements: unknown;
  published: boolean;
  version: number;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export async function operatorSignIn(input: {
  serverUrl: string;
  login: string;
  password: string;
  key: PublicKeyInfo;
}): Promise<StaffSession> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/op/v1/sessions'),
    body: {
      login: input.login,
      password: input.password,
      public_key: { alg: input.key.alg, key: input.key.key, id: input.key.id },
      platform: 'web',
    },
  });
  const operator = result.body['operator'];
  return {
    token: str(result.body['token']),
    expiresAtMs: Date.parse(str(result.body['expires_at'])) || Date.now() + 3_600_000,
    operatorName:
      typeof operator === 'object' && operator !== null
        ? str((operator as Record<string, unknown>)['display_name'])
        : '',
  };
}

export async function operatorThreads(input: {
  serverUrl: string;
  token: string;
}): Promise<StaffThread[]> {
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, '/op/v1/threads'),
    signingAccountId: STAFF_KEY_ALIAS,
    token: input.token,
  });
  const rows = result.body['threads'];
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row: unknown) => {
    const record = (row ?? {}) as Record<string, unknown>;
    const lastEvent = (record['last_event'] ?? {}) as Record<string, unknown>;
    const time = str(lastEvent['time']);
    return {
      threadId: str(record['thread_id']),
      buyerName: str(record['customer_name'], str(record['number'])),
      lastEventTimeMs: time.length > 0 ? Date.parse(time) : undefined,
    };
  });
}

function parseStaffView(json: Record<string, unknown>): StaffView {
  return {
    viewId: str(json['view_id']),
    title: str(json['title']),
    elements: parseElements(json['elements']),
    rawElements: json['elements'],
    published: json['published'] === true,
    version: typeof json['version'] === 'number' ? json['version'] : 0,
  };
}

const viewsPath = (threadId: string): string =>
  `/op/v1/threads/${encodeURIComponent(threadId)}/views`;

export async function operatorListViews(input: {
  serverUrl: string;
  token: string;
  threadId: string;
}): Promise<StaffView[]> {
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, viewsPath(input.threadId)),
    signingAccountId: STAFF_KEY_ALIAS,
    token: input.token,
  });
  const rows = result.body['views'];
  return Array.isArray(rows)
    ? rows.map((row: unknown) => parseStaffView((row ?? {}) as Record<string, unknown>))
    : [];
}

export async function operatorSaveView(input: {
  serverUrl: string;
  token: string;
  threadId: string;
  /** Absent creates a draft; present replaces one whole. A replacement rather than a
   *  patch, the same rule calendar events follow: a page assembled from partial edits
   *  has no moment at which anyone reviewed all of it. */
  viewId?: string;
  title: string;
  elements: unknown[];
}): Promise<StaffView> {
  const creating = input.viewId === undefined;
  const result = await send({
    method: creating ? 'POST' : 'PUT',
    url: joinPath(
      input.serverUrl,
      creating ? viewsPath(input.threadId) : `${viewsPath(input.threadId)}/${encodeURIComponent(input.viewId ?? '')}`,
    ),
    signingAccountId: STAFF_KEY_ALIAS,
    token: input.token,
    body: { title: input.title, elements: input.elements },
  });
  return parseStaffView(result.body);
}

export async function operatorPublishView(input: {
  serverUrl: string;
  token: string;
  threadId: string;
  viewId: string;
}): Promise<StaffView> {
  const result = await send({
    method: 'POST',
    url: joinPath(
      input.serverUrl,
      `${viewsPath(input.threadId)}/${encodeURIComponent(input.viewId)}/publish`,
    ),
    signingAccountId: STAFF_KEY_ALIAS,
    token: input.token,
  });
  return parseStaffView(result.body);
}

export async function operatorDeleteView(input: {
  serverUrl: string;
  token: string;
  threadId: string;
  viewId: string;
}): Promise<void> {
  await send({
    method: 'DELETE',
    url: joinPath(
      input.serverUrl,
      `${viewsPath(input.threadId)}/${encodeURIComponent(input.viewId)}`,
    ),
    signingAccountId: STAFF_KEY_ALIAS,
    token: input.token,
  });
}
